/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, SensorType, SensorUnits} from '@ion/core/src/types/entities';
import {isImperial} from '@ion/core/src/units/helpers';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {SCIDeviceSettingsUnits} from '@ion/dls/src/editors/DeviceEditorUtils';
import {SensorTypeIcon} from '@ion/dls/src/icons/SensorTypeIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {systemOfUnitsSelector} from '@/store/selectors/login-selectors';
import DeviceStatusLegends from './DeviceStatusLegends';
import {getCurrentStatusTimeStyles} from './statusTimeStyles';
import {getSensorDataAttributes} from './utils';

export interface CurrentSensorStatusProps {
  includeLegends?: boolean;
  data: {
    id: string,
    data: any[];
  };
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  statusContainer : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    padding        : '0 15px'
  },
  statusIcon : {
    color                          : dlsColor('grey3'),
    textTransform                  : 'uppercase',
    letterSpacing                  : '1px',
    display                        : 'flex',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column',
      alignItems    : 'flex-start'
    }
  },
  status : {
    [theme.breakpoints.down('sm')] : {
      padding : '5px 0'
    }
  },
  statusText : {
    fontSize       : '20px',
    width          : '100%',
    display        : 'flex',
    flexDirection  : 'column',
    alignContent   : 'center',
    justifyContent : 'flex-end',
    textAlign      : 'center',
    color          : dlsColor('aaBlue'),
    paddingTop     : '10px'
  },
  iconContainer : {
    display        : 'flex',
    flexDirection  : 'column',
    alignItems     : 'center',
    justifyContent : 'center',
    whiteSpace     : 'nowrap'

  },
  containerTopRow : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  icon : {
    paddingTop : '10px'
  },
  currentTime : {
    color          : dlsColor('grey3'),
    fontSize       : '10px',
    fontFamily     : 'Roboto',
    fontWeight     : 700,
    marginTop      : '2px',
    letterSpacing  : '1px',
    textTransform  : 'uppercase',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    padding        : '0 15px'
  }
}));

// current theme drops the legacy side padding, renders the status row as the
// shared bordered tile, and maps the big value's typography to the shared
// `valueText` style (its color stays inline - normal/alert are semantic, not
// the neutral tile value color) ...
const useCurrentStyles = makeStyles(() => {
  const {valueText, tile} = getAssetDetailsContentStyles();
  return {
    statusContainer : {
      ...tile,
      padding : '8px'
    },
    statusText : {
      ...valueText,
      paddingTop : 0
    },
    timeText : getCurrentStatusTimeStyles()
  };
});

const CurrentSensorStatus = ({data, includeLegends}: CurrentSensorStatusProps) => {
  const classes           = useStyles();
  const currentClasses    = useCurrentStyles();
  const isCurrent         = isCurrentTheme();
  const systemOfUnits     = useSelector(systemOfUnitsSelector);
  const uom               = isImperial(systemOfUnits?.value) ? SensorUnits.F : SensorUnits.C;
  const normal            = translate('tracking:deviceStatus.normal');
  const alert             = translate('tracking:deviceStatus.alert');
  const degree            = SCIDeviceSettingsUnits.Degree;
  const lumensUnit        = SCIDeviceSettingsUnits.Light;
  const shouldApplyMargin = useMemo(() => (
    data?.id === SensorType.Light || data?.id === SensorType.Humidity
  ), [data]);
  const {
    y: currentValue,
    eventTime: currentTime
  } = getSensorDataAttributes(data);

  const getCurrentValue = useCallback((data: GenericObject) => {
    const currentValues = {
      [SensorType.Temperature] : `${currentValue}${degree} ${uom}`,
      [SensorType.Humidity]    : `${currentValue}%`,
      [SensorType.Light]       : `${currentValue} ${lumensUnit}`
    };
    return currentValues[data?.id] ?? currentValue;
  }, [data, currentValue, degree, uom, lumensUnit]);

  const getCurrentColor = useCallback((data) => {
    const {max, min} = data?.data?.at(-1)?.threshold || data?.threshold;
    const point = data?.data?.at(-1)?.y;
    if (point > max || point < min) {
      return dlsColor('caRed');
    }
    return dlsColor('aaBlue');
  }, [data]);

  return (
    <>
      <div className={themedClassName(classes.statusContainer, currentClasses.statusContainer, isCurrent)}>
        <DeviceStatusLegends normalText={normal} thresholdText={alert} includeLegends={includeLegends}/>
        <div className={classes.iconContainer}>
          <div className={classes.containerTopRow}>
            <span className={classes.icon} style={{marginRight: `${shouldApplyMargin && '5px'}`}}>
              <SensorTypeIcon sensorType={data?.id} color={getCurrentColor(data)} size={32}/>
            </span>
            <div className={themedClassName(classes.statusText, currentClasses.statusText, isCurrent)}
              style={{color: `${getCurrentColor(data)}`}}>
              {getCurrentValue(data)}
            </div>
          </div>
        </div>
      </div>
      <span className={themedClassName(classes.currentTime, currentClasses.timeText, isCurrent)}
        style={{color: `${getCurrentColor(data)}`}}>
        {getFormattedDate(currentTime)}
      </span>
    </>
  );
};

export default CurrentSensorStatus;
